import { useState, useRef, useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import useAppStore from '../../store/app.store';
import useTema from '../../hooks/useTema';
import { IcMenu, IcSearch, IcBell, IcMessage, IcChevronDown, IcUser, IcLock, IcImage, IcLogOut, IcPalette, IcX } from '../ui/Icons';
import SearchModal from '../ui/SearchModal';
import NotificacoesPanel from '../ui/NotificacoesPanel';
import api from '../../services/api';
import { iniciar as iniciarSync, parar as pararSync, ouvirPendentes, enviarPendentes, COLECOES } from '../../services/sincronizacao';

const NIVEL_LABEL = { 1: 'Administrador', 2: 'Gerencial', 3: 'Operacional' };

function UserDropdown({ usuario, onClose }) {
  const logout = useAppStore(s => s.logout);
  const navigate = useNavigate();
  const t = useTema();

  const ir = (path) => { navigate(path); onClose(); };

  return (
    <div className="absolute right-0 top-full mt-2 w-56 rounded-xl overflow-hidden z-50"
      style={{ background: t.bgCardAlt, border: `1px solid ${t.border}`, boxShadow: '0 10px 30px rgba(0,0,0,.25)' }}>

      {/* Cabeçalho do usuário */}
      <div className="px-4 py-3" style={{ borderBottom: `1px solid ${t.border}` }}>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0 overflow-hidden"
            style={{ background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)' }}>
            {usuario?.foto
              ? <img src={usuario.foto} alt="" className="w-full h-full object-cover" />
              : usuario?.nome?.charAt(0)
            }
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold truncate" style={{ color: t.text }}>{usuario?.nome}</p>
            <p className="text-xs" style={{ color: t.textMuted }}>{NIVEL_LABEL[usuario?.nivel_acesso]}</p>
          </div>
        </div>
      </div>

      {/* Opções */}
      <div className="py-1">
        {[[<IcUser size={15} />, 'Meu perfil'], [<IcLock size={15} />, 'Alterar senha'], [<IcImage size={15} />, 'Foto de perfil']].map(([icon, label]) => (
          <button key={label} onClick={() => ir('/conta')}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition-colors"
            style={{ color: t.textSecond }}
            onMouseEnter={e => e.currentTarget.style.background = t.bgCardHover}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            {icon} {label}
          </button>
        ))}
      </div>

      {/* Sair */}
      <div className="py-1" style={{ borderTop: `1px solid ${t.border}` }}>
        <button
          onClick={() => { logout(); onClose(); }}
          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition-colors"
          style={{ color: '#f87171' }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.08)'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
          <IcLogOut size={15} /> Sair da conta
        </button>
      </div>
    </div>
  );
}

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const dropdownRef = useRef(null);
  const notifRef    = useRef(null);
  const usuario = useAppStore(s => s.usuario);
  const chat_mensagens = useAppStore(s => s.chat_mensagens);
  const notificacoes   = useAppStore(s => s.notificacoes);
  const t = useTema();

  const chatNaoLidas = chat_mensagens
    ? chat_mensagens.filter(m => m.para_id === usuario?.id && !m.lida).length
    : 0;

  const notifNaoLidas = notificacoes
    ? notificacoes.filter(n => n.destinatario_id === usuario?.id && !n.lida).length
    : 0;

  // Financeiro, Contratos, Acompanhamento e Comercial: dados no banco (só admin)
  const [pendentesSync, setPendentesSync] = useState([]);
  const [enviandoSync, setEnviandoSync] = useState(false);
  useEffect(() => {
    if (!usuario?.id || usuario.nivel_acesso > 1) return undefined;
    iniciarSync();
    const sair = ouvirPendentes(setPendentesSync);
    return () => { sair(); pararSync(); };
  }, [usuario?.id, usuario?.nivel_acesso]);

  const modulosPendentes = [...new Set(pendentesSync.map((c) => COLECOES[c]))].join(', ');

  // Notificações do servidor (demandas recebidas, tarefas passadas) a cada minuto
  useEffect(() => {
    if (!usuario?.id) return undefined;
    const buscar = () => api.get('/notificacoes')
      .then((r) => { if (Array.isArray(r.data)) useAppStore.setState({ notificacoes: r.data }); })
      .catch(() => {});
    buscar();
    const id = setInterval(buscar, 60000);
    return () => clearInterval(id);
  }, [usuario?.id]);

  // Fecha dropdown ao clicar fora
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Atalho Ctrl+K para busca
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: t.bgPage }}>
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Topbar */}
        <header className="flex-shrink-0 flex items-center gap-2 sm:gap-4 px-3 sm:px-6 h-[60px]"
          style={{ background: t.bgCard, borderBottom: `1px solid ${t.border}` }}>

          <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 rounded-lg flex-shrink-0"
            style={{ color: t.textMuted }}><IcMenu size={20} /></button>

          {/* Busca — escondida no mobile, visível no desktop */}
          <div className="hidden sm:flex flex-1 justify-center">
            <button onClick={() => setSearchOpen(true)} className="relative w-full max-w-md flex items-center"
              style={{ background: t.bgInput, border: `1px solid ${t.border}`, borderRadius: 12, padding: '8px 12px 8px 36px', cursor: 'text' }}>
              <span className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: t.textMuted }}><IcSearch size={14} /></span>
              <span className="text-sm flex-1 text-left" style={{ color: t.textMuted }}>Buscar no sistema...</span>
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
                style={{ background: t.border, color: t.textMuted }}>Ctrl + K</span>
            </button>
          </div>

          {/* No mobile: flex-1 empurra ações para a direita */}
          <div className="flex-1 sm:hidden" />

          {/* Ações */}
          <div className="flex items-center gap-1 sm:gap-2">
            {/* Botão de busca no mobile */}
            <button onClick={() => setSearchOpen(true)} className="sm:hidden w-9 h-9 flex items-center justify-center rounded-xl"
              style={{ background: t.bgInput, color: t.textSecond }}>
              <IcSearch size={17} />
            </button>
            <div className="relative" ref={notifRef}>
              <button onClick={() => setNotifOpen(o => !o)}
                className="relative w-9 h-9 flex items-center justify-center rounded-xl"
                style={{ background: notifOpen ? t.bgCardHover : t.bgInput, color: t.textSecond }}>
                <IcBell size={17} />
                {notifNaoLidas > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 flex items-center justify-center rounded-full text-white text-[9px] font-bold"
                    style={{ background: 'linear-gradient(135deg,#8b5cf6,#6d28d9)' }}>
                    {notifNaoLidas > 9 ? '9+' : notifNaoLidas}
                  </span>
                )}
              </button>
              {notifOpen && <NotificacoesPanel onClose={() => setNotifOpen(false)} />}
            </div>

            {chatNaoLidas > 0 && (
              <button className="relative w-9 h-9 flex items-center justify-center rounded-xl"
                style={{ background: t.bgInput, color: t.textSecond }}>
                <IcMessage size={17} />
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 flex items-center justify-center rounded-full bg-red-500 text-white text-[9px] font-bold">
                  {chatNaoLidas > 9 ? '9+' : chatNaoLidas}
                </span>
              </button>
            )}

            {/* Avatar + dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(o => !o)}
                className="flex items-center gap-2 pl-2 rounded-xl py-1 pr-2 transition-colors"
                style={{ background: dropdownOpen ? t.bgCardHover : 'transparent' }}>
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold overflow-hidden"
                  style={{ background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)' }}>
                  {usuario?.foto
                    ? <img src={usuario.foto} alt="" className="w-full h-full object-cover" />
                    : usuario?.nome?.charAt(0)
                  }
                </div>
                <span style={{ color: t.textMuted, transition: 'transform 0.2s', display: 'inline-flex', transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}><IcChevronDown size={14} /></span>
              </button>

              {dropdownOpen && (
                <UserDropdown usuario={usuario} onClose={() => setDropdownOpen(false)} />
              )}
            </div>
          </div>
        </header>

        {/* Conteúdo principal */}
        <main className="flex-1 overflow-y-auto" style={{ background: t.bgPage }}>
          <div className="p-4 md:p-6 max-w-screen-2xl mx-auto">
            {pendentesSync.length > 0 && (
              <div style={{ background: 'rgba(245,158,11,.1)', border: '1px solid rgba(245,158,11,.35)', borderRadius: 12, padding: '12px 16px', marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <p style={{ flex: 1, minWidth: 240, fontSize: 13, color: t.text }}>
                  <strong>{modulosPendentes}</strong>: os dados deste navegador ainda não estão no servidor.
                  Envie a partir do computador onde você usa esses módulos, para não perder nada.
                </p>
                <button className="btn-primary" disabled={enviandoSync}
                  onClick={async () => { setEnviandoSync(true); try { await enviarPendentes(); } finally { setEnviandoSync(false); } }}>
                  {enviandoSync ? 'Enviando...' : 'Enviar para o servidor'}
                </button>
              </div>
            )}
            <Outlet />
          </div>
        </main>
      </div>

      {searchOpen && <SearchModal onClose={() => setSearchOpen(false)} />}
    </div>
  );
}
